import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Col,
  Container,
  Form,
  Modal,
  OverlayTrigger,
  Row,
  Table,
  Tooltip,
} from "react-bootstrap";
import moment from "moment";
import Pagination from "react-js-pagination";
import swal from "sweetalert";
import { ThreeDotSpinner } from "../loader";
import { apiService } from "../../service/api.service";
import { dateFormate } from "../../config/config";

const DEFAULT_LIMIT = 20;
const LIMIT_OPTIONS = [10, 20, 50, 100];

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "verify", label: "Verify" },
  { value: "rejected", label: "Rejected" },
];

const REASON_OPTIONS = [
  { value: "", label: "All Rejection Reasons" },
  { value: "missing_message", label: "Missing Message" },
  { value: "missing_from_number", label: "Missing From Number" },
  { value: "disallowed_prefix", label: "Disallowed Prefix" },
  { value: "invalid_keyword", label: "Invalid Keyword" },
  { value: "missing_email", label: "Missing Email" },
  { value: "invalid_email", label: "Invalid Email" },
  { value: "extra_tokens", label: "Extra Tokens" },
  { value: "invalid_declared_phone", label: "Invalid Declared Phone" },
  { value: "declared_phone_mismatch", label: "Declared Phone Mismatch" },
  { value: "user_not_found", label: "User Not Found" },
  { value: "number_owned_by_other_user", label: "Number Owned By Other User" },
  { value: "number_already_verified", label: "Number Already Verified" },
  { value: "carrier_lookup_error", label: "Carrier Lookup Error" },
  { value: "carrier_info_missing", label: "Carrier Info Missing" },
  { value: "disallowed_carrier_type", label: "Disallowed Carrier Type" },
  { value: "disallowed_carrier_name", label: "Disallowed Carrier Name" },
  { value: "unexpected_error", label: "Unexpected Error" },
];

export const TwilioResponses = () => {
  const [responses, setResponses] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [totalItems, setTotalItems] = useState(0);
  const [fromFilter, setFromFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [reasonFilter, setReasonFilter] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({
    from: "",
    status: "",
    reason: "",
  });
  const [loader, setLoader] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState(null);

  const reasonLookup = useMemo(() => {
    return REASON_OPTIONS.reduce((acc, option) => {
      if (option.value) {
        acc[option.value] = option.label;
      }
      return acc;
    }, {});
  }, []);

  useEffect(() => {
    let isActive = true;

    const loadResponses = async () => {
      setLoader(true);
      try {
        const response = await apiService.getTwilioResponses({
          page,
          limit,
          from: appliedFilters.from,
          status: appliedFilters.status,
          reason: appliedFilters.reason,
        });

        if (!isActive) {
          return;
        }

        const payload = response?.data || {};
        const list = payload?.data || [];
        const pagination = payload?.pagination || {};

        setResponses(list);
        setTotalItems(pagination.total || 0);

        if (pagination.page && pagination.page !== page) {
          setPage(pagination.page);
        }
        if (pagination.limit && pagination.limit !== limit) {
          setLimit(pagination.limit);
        }
      } catch (error) {
        if (!isActive) {
          return;
        }

        if (error?.response?.status === 401) {
          swal({ icon: "error", text: "Unauthorized", button: "OK" }).then(
            () => {
              localStorage.clear();
              window.location.href = "/";
            }
          );
        } else {
          swal({
            icon: "error",
            text:
              error?.response?.data?.message ||
              error?.message ||
              "Failed to fetch Twilio responses",
            button: "OK",
          });
        }
      } finally {
        if (isActive) {
          setLoader(false);
        }
      }
    };

    loadResponses();

    return () => {
      isActive = false;
    };
  }, [page, limit, appliedFilters]);

  const handleSearch = (event) => {
    event?.preventDefault();
    setPage(1);
    setAppliedFilters({
      from: fromFilter.trim(),
      status: statusFilter,
      reason: statusFilter === "rejected" ? reasonFilter : "",
    });
  };

  const handleReset = () => {
    setFromFilter("");
    setStatusFilter("");
    setReasonFilter("");
    setLimit(DEFAULT_LIMIT);
    setPage(1);
    setAppliedFilters({
      from: "",
      status: "",
      reason: "",
    });
  };

  const handlePageChange = (nextPage) => {
    if (page !== nextPage) {
      setPage(nextPage);
    }
  };

  const handleLimitChange = (event) => {
    const nextLimit = Number(event.target.value) || DEFAULT_LIMIT;
    if (limit !== nextLimit) {
      setLimit(nextLimit);
      setPage(1);
    }
  };

  const openDetails = (item) => {
    setSelectedResponse(item);
    setShowDetails(true);
  };

  const closeDetails = () => {
    setShowDetails(false);
    setSelectedResponse(null);
  };

  return (
    <div className="dashboard-main-area">
      {loader && <ThreeDotSpinner />}
      <Container fluid>
        <div className="dashboard-area-heading management-heading">
          <Row className="row justify-content-center align-items-center mb-3">
            <Col md={12} lg={11}>
              <div className="heading-top-area">
                <div className="d-flex">
                  <h2 className="align-self-center m-0">Twilio Responses</h2>
                </div>
              </div>
            </Col>
          </Row>

          <Row className="pb-3 justify-content-center">
            <Col md={12} lg={11}>
              <Form
                onSubmit={handleSearch}
                className="filter-form d-flex flex-wrap align-items-end gap-3"
              >
                <Form.Group controlId="fromFilter">
                  <Form.Label>From Number</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="+11234567890"
                    value={fromFilter}
                    onChange={(event) => setFromFilter(event.target.value)}
                  />
                </Form.Group>

                <Form.Group controlId="statusFilter">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={statusFilter}
                    onChange={(event) => {
                      const value = event.target.value;
                      setStatusFilter(value);
                      if (value !== "rejected") {
                        setReasonFilter("");
                      }
                    }}
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group controlId="reasonFilter">
                  <Form.Label>Reason</Form.Label>
                  <Form.Select
                    value={reasonFilter}
                    onChange={(event) => setReasonFilter(event.target.value)}
                    disabled={statusFilter !== "rejected"}
                  >
                    {REASON_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group controlId="limitSelect">
                  <Form.Label>Rows Per Page</Form.Label>
                  <Form.Select value={limit} onChange={handleLimitChange}>
                    {LIMIT_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <div className="d-flex gap-2 align-self-center">
                  <Button variant="primary" type="submit">
                    Apply
                  </Button>
                  <Button variant="outline-secondary" onClick={handleReset}>
                    Reset
                  </Button>
                </div>
              </Form>
            </Col>
          </Row>

          <Row className="pb-4 justify-content-center">
            <Col md={12} lg={11}>
              <div className="audit-request-box">
                <Table responsive="sm" className="UserListTable">
                  <thead>
                    <tr>
                      <th>Sr. No.</th>
                      <th>From</th>
                      <th>User Message</th>
                      <th>Reply Message</th>
                      <th>Reason</th>
                      <th>Status</th>
                      <th>Created At</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {responses && responses.length > 0 ? (
                      responses.map((item, index) => {
                        const serial = (page - 1) * limit + index + 1;
                        const reason = item?.reason || null;
                        const reasonLabel = reason
                          ? reasonLookup[reason] || reason
                          : "—";
                        const statusLabel =
                          item?.status === "verify" ? "Verify" : "Rejected";

                        return (
                          <tr key={item?._id || index}>
                            <td>{serial}</td>
                            <td>{item?.from || "N/A"}</td>
                            <td className="text-break twilio-message-cell">
                              {item?.incomingMessage || "N/A"}
                            </td>
                            <td className="text-break twilio-message-cell">
                              {item?.message || "N/A"}
                            </td>
                            <td>{reasonLabel}</td>
                            <td>{statusLabel}</td>
                            <td>
                              {item?.createdAt
                                ? moment(item.createdAt).format(dateFormate)
                                : "N/A"}
                            </td>
                            <td>
                              <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip id="tooltip-view">View</Tooltip>}
                              >
                                <Button
                                  variant="link"
                                  className="p-0 twilio-view-btn"
                                  onClick={() => openDetails(item)}
                                >
                                  View
                                </Button>
                              </OverlayTrigger>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr className="no-dat-found-box">
                        <td colSpan={8}>
                          <img
                            src={
                              require("../../assets/images/no-data.svg").default
                            }
                            alt="no-data"
                          />
                          <h3 className="text-center text-light">
                            No data found
                          </h3>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>

                {responses && responses.length > 0 ? (
                  <Pagination
                    activePage={page}
                    itemsCountPerPage={limit}
                    totalItemsCount={totalItems}
                    pageRangeDisplayed={5}
                    onChange={handlePageChange}
                    prevPageText={"Prev"}
                    nextPageText={"Next"}
                  />
                ) : null}
              </div>
            </Col>
          </Row>
        </div>
      </Container>

      <Modal show={showDetails} onHide={closeDetails} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Twilio Response Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="mb-3">
            <Col md={6}>
              <strong>From:</strong> {selectedResponse?.from || "N/A"}
            </Col>
            <Col md={12}>
              <strong>User Message:</strong>
              <div className="mt-2 twilio-detail-block">
                {selectedResponse?.incomingMessage || "N/A"}
              </div>
            </Col>
            <Col md={12}>
              <strong>Reply Message:</strong>
              <div className="mt-2 twilio-detail-block">
                {selectedResponse?.message || "N/A"}
              </div>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <strong>Status:</strong>{" "}
              {selectedResponse?.status === "verify" ? "Verify" : "Rejected"}
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <strong>Reason:</strong>{" "}
              {selectedResponse?.reason
                ? reasonLookup[selectedResponse.reason] ||
                  selectedResponse.reason
                : "—"}
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeDetails}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TwilioResponses;
